import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useCVStore } from '../store/cvStore'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Textarea } from '../components/ui/textarea'
import { toast } from 'sonner'
import { ArrowLeft, BarChart3, Loader2 } from 'lucide-react'

export default function CVAnalysisPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const {
    currentCV, cvData, analysis, loading,
    fetchCV, analyzeCV, clearCurrent,
  } = useCVStore()

  const [jobDescription, setJobDescription] = useState('')
  const [hasRun, setHasRun] = useState(false)

  useEffect(() => {
    if (id) {
      fetchCV(id)
    } else {
      clearCurrent()
    }
  }, [id])

  useEffect(() => {
    if (currentCV?.job_description) {
      setJobDescription(currentCV.job_description)
    }
  }, [currentCV])

  const handleAnalyze = async () => {
    if (!cvData) {
      toast.error('No CV data available. Save your CV first.')
      return
    }
    if (!jobDescription) {
      toast.error('Enter a job description first')
      return
    }
    await analyzeCV(cvData, jobDescription)
    setHasRun(true)
    toast.success('Analysis complete')
  }

  const backPath = id ? `/editor/${id}` : '/editor'

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(backPath)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">
            AI Analysis
          </h1>
        </div>
        <div className="text-sm text-gray-500">
          {currentCV?.name || cvData?.personalInfo?.name || 'Untitled CV'}
        </div>
      </div>

      {/* Job Description Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-blue-600" />
            Job Description
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            className="min-h-[200px]"
            placeholder="Paste the full job description here to analyze how well your CV matches..."
          />
          <Button
            onClick={handleAnalyze}
            disabled={loading || !jobDescription}
            size="lg"
            className="w-full gap-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <BarChart3 className="w-4 h-4" />
            )}
            {loading ? 'Analyzing...' : 'Analyze CV Match'}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {hasRun && analysis && (
        <div className="space-y-6">
          {/* Score Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-blue-600">{analysis.matchAnalysis.score || 0}%</div>
                <div className="text-sm text-gray-500 mt-1">Overall Match</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-indigo-600">{analysis.matchAnalysis.keywordsMatch || 0}%</div>
                <div className="text-sm text-gray-500 mt-1">Keyword Match</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-teal-600">{analysis.matchAnalysis.skillsMatch || 0}%</div>
                <div className="text-sm text-gray-500 mt-1">Skills Match</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-purple-600">{analysis.matchAnalysis.experienceMatch || 0}%</div>
                <div className="text-sm text-gray-500 mt-1">Experience Match</div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Matched Keywords */}
            {analysis.matchAnalysis.matchedKeywords?.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Matched Keywords</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.matchAnalysis.matchedKeywords.map((kw, i) => (
                      <span key={i} className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full">
                        {kw}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Missing Keywords */}
            {analysis.matchAnalysis.missingKeywords?.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Missing Keywords</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.matchAnalysis.missingKeywords.map((kw, i) => (
                      <span key={i} className="text-sm bg-red-100 text-red-700 px-3 py-1 rounded-full">
                        {kw}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Gaps */}
          {analysis.matchAnalysis.gaps?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Identified Gaps</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {analysis.matchAnalysis.gaps.map((g, i) => (
                    <li key={i} className="flex gap-2 text-sm text-gray-700">
                      <span className="text-orange-500 shrink-0 mt-0.5">•</span>
                      {g}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Recommendations */}
          {analysis.recommendations?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Recommendations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {analysis.recommendations.map((rec, i) => (
                  <div key={i} className="flex gap-3">
                    <div className={`shrink-0 mt-1 w-2 h-2 rounded-full ${
                      rec.priority === 'high' ? 'bg-green-500' :
                      rec.priority === 'medium' ? 'bg-yellow-500' : 'bg-red-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 font-medium">{rec.text}</p>
                      <p className="text-xs text-gray-400 capitalize mt-0.5">
                        {rec.section} · {rec.priority} priority
                      </p>
                      {rec.suggestions && rec.suggestions.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {rec.suggestions.map((s, j) => (
                            <li key={j} className="text-sm text-gray-600 flex gap-2">
                              <span className="text-gray-300 shrink-0">→</span>
                              {s}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Empty State */}
      {!hasRun && cvData && (
        <Card>
          <CardContent className="py-12 text-center">
            <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-1">Ready to Analyze</h3>
            <p className="text-sm text-gray-400">
              Paste a job description above and click "Analyze CV Match" to get started.
            </p>
          </CardContent>
        </Card>
      )}

      {!cvData && !loading && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-gray-500">No CV data available. Save your CV in the editor first.</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate(backPath)}>
              Go to Editor
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
